import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import ReasonPromptModal from '@/components/admin/ReasonPromptModal'
import {
  adminRelationshipService,
  AdminRelationship,
  AdminRelationshipListPagination
} from '@/services/adminRelationshipService'
import { RelationshipStatus } from '@/types/matching'
import { useToast } from '@/contexts/ToastContext'

const STATUS_OPTIONS: Array<{ label: string; value: RelationshipStatus | 'all' }> = [
  { label: 'All statuses', value: 'all' },
  { label: 'Pending', value: RelationshipStatus.PENDING },
  { label: 'Active', value: RelationshipStatus.ACTIVE },
  { label: 'Completed', value: RelationshipStatus.COMPLETED },
  { label: 'Cancelled', value: RelationshipStatus.CANCELLED }
]

const AdminRelationshipsPage: React.FC = () => {
  const { showToast } = useToast()

  const [relationships, setRelationships] = useState<AdminRelationship[]>([])
  const [pagination, setPagination] = useState<AdminRelationshipListPagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [mentorIdFilter, setMentorIdFilter] = useState('')
  const [menteeIdFilter, setMenteeIdFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<RelationshipStatus | 'all'>('all')
  const [page, setPage] = useState(1)
  const [forceEndTarget, setForceEndTarget] = useState<AdminRelationship | null>(null)
  const [isSubmittingForceEnd, setIsSubmittingForceEnd] = useState(false)

  const fetchingRef = useRef(false)

  const fetchRelationships = useCallback(async () => {
    if (fetchingRef.current) return

    try {
      fetchingRef.current = true
      setLoading(true)
      const result = await adminRelationshipService.listRelationships({
        mentorId: mentorIdFilter || undefined,
        menteeId: menteeIdFilter || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        page,
        limit: 20
      })
      setRelationships(result.relationships)
      setPagination(result.pagination)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch relationships'
      })
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [mentorIdFilter, menteeIdFilter, statusFilter, page, showToast])

  useEffect(() => {
    fetchRelationships()
  }, [fetchRelationships])

  useEffect(() => {
    setPage(1)
  }, [mentorIdFilter, menteeIdFilter, statusFilter])

  const handleForceEndConfirm = async (reason: string) => {
    if (!forceEndTarget) return

    try {
      setIsSubmittingForceEnd(true)
      await adminRelationshipService.forceEndRelationship(forceEndTarget.id, reason)
      showToast({
        type: 'success',
        message: `Relationship between ${forceEndTarget.mentor.firstName} ${forceEndTarget.mentor.lastName} and ${forceEndTarget.mentee.firstName} ${forceEndTarget.mentee.lastName} has been ended.`
      })
      setForceEndTarget(null)
      await fetchRelationships()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to force-end relationship'
      })
    } finally {
      setIsSubmittingForceEnd(false)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900">Relationships</h2>
      <p className="mt-2 text-gray-600">View all mentor-mentee relationships on the platform.</p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Filter by mentor ID..."
          value={mentorIdFilter}
          onChange={(e) => setMentorIdFilter(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <input
          type="text"
          placeholder="Filter by mentee ID..."
          value={menteeIdFilter}
          onChange={(e) => setMenteeIdFilter(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as RelationshipStatus | 'all')}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <Card className="mt-6">
        <CardBody>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
          ) : relationships.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No relationships found.</p>
          ) : (
            <>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 text-sm text-gray-500">
                    <th className="pb-2">Mentor</th>
                    <th className="pb-2">Mentee</th>
                    <th className="pb-2">Status</th>
                    <th className="pb-2">Requested</th>
                    <th className="pb-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {relationships.map((relationship) => {
                    const canForceEnd =
                      relationship.status === RelationshipStatus.PENDING || relationship.status === RelationshipStatus.ACTIVE

                    return (
                      <tr key={relationship.id} className="border-b border-gray-100 last:border-0">
                        <td className="py-3">
                          <div className="font-medium text-gray-900">
                            {relationship.mentor.firstName} {relationship.mentor.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{relationship.mentor.email}</div>
                        </td>
                        <td className="py-3">
                          <div className="font-medium text-gray-900">
                            {relationship.mentee.firstName} {relationship.mentee.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{relationship.mentee.email}</div>
                        </td>
                        <td className="py-3 text-gray-600 capitalize">{relationship.status}</td>
                        <td className="py-3 text-gray-600">
                          {new Date(relationship.requestedAt).toLocaleDateString()}
                        </td>
                        <td className="py-3">
                          {canForceEnd && (
                            <Button size="sm" variant="danger" onClick={() => setForceEndTarget(relationship)}>
                              Force End
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <span className="text-sm text-gray-500">
                    Page {pagination.page} of {pagination.totalPages} ({pagination.totalDocs} relationships)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={!pagination.hasPrevPage}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <ReasonPromptModal
        isOpen={forceEndTarget !== null}
        title="Force End Relationship"
        description={
          forceEndTarget
            ? `Ending the relationship between ${forceEndTarget.mentor.firstName} ${forceEndTarget.mentor.lastName} and ${forceEndTarget.mentee.firstName} ${forceEndTarget.mentee.lastName} requires a reason for the audit log.`
            : undefined
        }
        confirmLabel="Force End"
        confirmVariant="danger"
        onClose={() => setForceEndTarget(null)}
        onConfirm={handleForceEndConfirm}
        isSubmitting={isSubmittingForceEnd}
      />
    </div>
  )
}

export default AdminRelationshipsPage
